import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import '../styles/componentStyles/HorizontalSlider.css';

const values1to10 = [
  { id: 1, title: '1' },
  { id: 2, title: '2' },
  { id: 3, title: '3' },
  { id: 4, title: '4' },
  { id: 5, title: '5' },
  { id: 6, title: '6' },
  { id: 7, title: '7' },
  { id: 8, title: '8' },
  { id: 9, title: '9' },
  { id: 10, title: '10' },
];

const valuesStress = [
  { id: 1, title: '1' },
  { id: 2, title: '2' },
  { id: 3, title: '3' },
  { id: 4, title: '4' },
  { id: 5, title: '5' },
];

function Slider({ values = [], ...props }) {
  const { t } = useTranslation();
  const {
    scale1to10,
    questionid,
    data,
    saveResponse,
    isActivityTracker,
    wheelchair,
    sliderSteps,
    handleSliderSteps,
    customValues,
    isReversed,
    labels,
  } = props;

  const [value, setValue] = useState('');
  const [valuesArr, setValuesArr] = useState([]);
  const [leftLabel, setLeftLabel] = useState('');
  const [rightLabel, setRightLabel] = useState('');
  const [valueText, setValueText] = useState('');

  useEffect(() => {
    if (isActivityTracker) {
      setValue(sliderSteps || 0);
    } else if (scale1to10) {
      setValuesArr([...values1to10]);
      if (data?.[questionid] && data?.bar_index) {
        setValue(values1to10[Number(data?.bar_index) - 1]);
      } else {
        setValue(values1to10[4]);
      }
    } else if (customValues) {
      setValuesArr([...customValues]);
      if (data?.[questionid] && data?.bar_index) {
        setValue(customValues[Number(data?.bar_index) - 1]);
      } else {
        setValue(customValues[2]);
      }
    } else {
      setValuesArr([...valuesStress]);
      if (data?.[questionid] && data?.bar_index) {
        setValue(valuesStress[Number(data?.bar_index) - 1]);
      } else {
        setValue(valuesStress[2]);
      }
    }
  }, [data, questionid, isActivityTracker, scale1to10, sliderSteps, customValues]);

  const handleSave = (newValue) => {
    if (isActivityTracker) {
      handleSliderSteps(Number(newValue));
    } else {
      const newValueObj = valuesArr[Number(newValue) - 1];
      setValue(newValueObj);
      saveResponse('data', {
        id: questionid,
        value: newValueObj.id,
      });
      saveResponse('data', {
        id: 'bar_index',
        value: newValue,
      });
    }
  };

  const getDisplayValue = useCallback(() => {
    if (isActivityTracker) {
      if (wheelchair) {
        const miles = (Number(value) / 1250) * 0.621371;
        return {
          main: `${Math.round(miles * 10) / 10}`,
          alt: `${Math.round(miles * 1.60934 * 10) / 10} ${t('T552')}`, // km
        };
      }
      return {
        main: Math.round(Number(value)).toLocaleString(),
        alt: `${((Number(value) / 1250) * 0.621371).toFixed(1)} ${t('T094')}`, // miles
      };
    }
    return null;
  }, [isActivityTracker, wheelchair, value, t]);

  const getSliderValue = () => {
    if (isActivityTracker) return value;
    if (value) return value.id;
    return scale1to10 ? 5 : 1;
  };

  const getAriaValueNow = () => {
    if (isActivityTracker) return value;
    if (value) return value.id;
    return scale1to10 ? 5 : 1;
  };

  const getAriaValueMax = () => {
    if (isActivityTracker) return 30000;
    return scale1to10 ? 10 : 5;
  };

  useEffect(() => {
    if (isActivityTracker) {
      setValueText(`${getDisplayValue()?.main} ${wheelchair ? t('T551') : t('T092')}`);
    } else if (value?.label) {
      setValueText(t(value.label));
    } else if (value?.title) {
      setValueText(value.title);
    } else if (scale1to10) {
      setValueText(5);
    }
  }, [value, isActivityTracker, scale1to10, wheelchair, getDisplayValue, t]);

  useEffect(() => {
    if (isActivityTracker) {
      setLeftLabel(`0 ${t('T094')}`);
      setRightLabel(`14.9 ${t('T094')}`);
    } else if (scale1to10) {
      setLeftLabel(`${labels && labels[0] ? t(labels[0]) : t('T044')} (${values1to10[0].title})`);
      setRightLabel(`${labels && labels[1] ? t(labels[1]) : t('T045')} (${values1to10[9].title})`);
    } else if (valuesArr.length > 0) {
      setLeftLabel(valuesArr[0]?.title);
      setRightLabel(valuesArr[valuesArr.length - 1]?.title);
    }
  }, [valuesArr, labels, isActivityTracker, scale1to10, t]);

  return (
    <div className={`horizontal-slider-wrapper ${scale1to10 ? 'scale-1-to-10' : ''}`}>
      {scale1to10 && !isActivityTracker && (
        <div className="horizontal-slider-scale">
          <p>
            {labels && labels[0] ? t(labels[0]) : t('T044')} ({values1to10[0].title})
          </p>
          <p>
            {labels && labels[1] ? t(labels[1]) : t('T045')} ({values1to10[9].title})
          </p>
        </div>
      )}
      {customValues && (
        <div className="horizontal-slider-scale scale-custom">
          <p>{customValues[0].title}</p>
          <p>{customValues[4].title}</p>
        </div>
      )}

      {isActivityTracker && (
        <div className="activity-display">
          <p className="main-value">
            {getDisplayValue()?.main} {wheelchair ? t('T551') : t('T092')}
          </p>
          <p className="alt-value">{getDisplayValue()?.alt}</p>
        </div>
      )}

      <span className="horizontal-slider-container">
        <input
          id="horizontal-slider"
          type="range"
          className={`horizontal-slider ${isReversed ? 'reversed' : ''}`}
          name="temp"
          step={isActivityTracker ? '100' : '1'}
          min={isActivityTracker ? '0' : '1'}
          max={getAriaValueMax()}
          value={getSliderValue()}
          onChange={(e) => {
            const newValue = e.target.value;
            if (isActivityTracker) {
              setValue(newValue);
              handleSave(newValue);
            } else {
              const newValueObj = valuesArr[Number(newValue) - 1];
              setValue(newValueObj);
              handleSave(newValue);
            }
          }}
          aria-labelledby={props['aria-labelledby']}
          aria-valuemin={isActivityTracker ? '0' : '1'}
          aria-valuemax={getAriaValueMax()}
          aria-valuenow={getAriaValueNow()}
          aria-valuetext={valueText}
          aria-label={`${props.title} ${t('slider.description.from')} ${leftLabel} ${t('slider.description.to')} ${rightLabel}`}
        />
      </span>

      {!isActivityTracker && (
        <div className="horizontal-slider-dataset center">
          <span>{!customValues ? getSliderValue() : valueText}</span>
        </div>
      )}

      {isActivityTracker && (
        <div className="horizontal-slider-dataset">
          {values.map((val) => (
            <span key={val.value}>{val.label}</span>
          ))}
        </div>
      )}
    </div>
  );
}

export default Slider;
